import { createMutationAtom } from 'atoms/create-resource-atom';
import { atom } from 'jotai';
import request from 'lib/request';
import { BulkImportResponse, CreateAccountResponse } from 'types/api-responses';

interface EnergyLinkAccount {
  id: number;
  username: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface BulkImportParams {
  accountId: string;
  days: number;
}

interface AccountsState {
  loading: boolean;
  results: EnergyLinkAccount[];
  initialized: boolean;
  error?: Error;
}

const ACCOUNTS_URL = '/energy_link/accounts';
const BULK_IMPORT_URL = '/energy_link/bulk_imports';

const accountsStateAtom = atom<AccountsState>({
  loading: false,
  results: [],
  initialized: false,
});

const accountsAtom = atom(
  (get) => get(accountsStateAtom),
  async (get, set) => {
    const currentState = get(accountsStateAtom);
    set(accountsStateAtom, { ...currentState, loading: true });

    try {
      const response = await request.get<EnergyLinkAccount[]>(ACCOUNTS_URL);
      if ('error' in response) {
        throw new Error('Failed to fetch accounts');
      }

      set(accountsStateAtom, {
        loading: false,
        results: response,
        initialized: true,
      });
    } catch (error) {
      set(accountsStateAtom, {
        loading: false,
        results: [],
        initialized: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      });
      throw error;
    }
  }
);

const bulkImportAtom = createMutationAtom<
  BulkImportResponse,
  'energyLinkBulkImport'
>();

const selectedDaysAtom = atom<number>(90);

const createAccountAtom = createMutationAtom<
  CreateAccountResponse,
  'energyLinkCreateAccount'
>();

export {
  ACCOUNTS_URL,
  accountsAtom,
  BULK_IMPORT_URL,
  bulkImportAtom,
  createAccountAtom,
  selectedDaysAtom,
  type BulkImportParams,
  type EnergyLinkAccount,
};
